import { unwrapResult } from '@reduxjs/toolkit';
import { message } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'shared/store';
import {
  fetchMoreChatMessages,
  selectHasMoreChatMessages,
  selectVisitorChatMessages,
} from 'shared/store/chat-slice';
import { ActiveVisitor } from 'types';
import { UniqueVisitor } from 'sb-lib/types';
import { FadeScroller } from '../FadeScroller';
import { SessionChatMessage } from './SessionChatMessage';
import { SessionChatSystemMessage } from './SessionChatSystemMessage';
import { SessionChatBotMessage } from './SessionChatBotMessage';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import { format, isSameDay } from 'date-fns';
import './style.less';

interface Props {
  visitor: ActiveVisitor | UniqueVisitor | null;
  isChatCollapsed?: boolean;
}

export const SessionChatMessages: React.FC<Props> = ({
  visitor,
  isChatCollapsed = false,
}) => {
  const dispatch = useAppDispatch();
  const { conversation } = useAppSelector(s => s.conversation);
  const hasNextPage = useAppSelector(selectHasMoreChatMessages);
  const messages = useAppSelector(s => selectVisitorChatMessages(s, visitor?.id));
  const members = useAppSelector(selectActiveOrgMembers);
  const [loadingMore, setLoadingMore] = useState(false);
  const aiPersonas = useAppSelector(s => s.aiChat.aiPersonas);

  const aiPersonaAgent = useMemo(() => {
    return aiPersonas.find(p => p.id === conversation?.aiPersonaId);
  }, [aiPersonas, conversation?.aiPersonaId]);

  const onLoadMore = useCallback(async () => {
    if (!visitor?.id || !conversation?.id) {
      return;
    }
    setLoadingMore(true);
    try {
      const actionResult = await dispatch(
        fetchMoreChatMessages({ visitorId: visitor.id, conversationId: conversation.id }),
      );
      unwrapResult(actionResult);
    } catch (e) {
      message.error(`Problem getting chat history: ${e}`);
    } finally {
      setLoadingMore(false);
    }
  }, [dispatch, conversation?.id, visitor?.id]);

  return (
    <FadeScroller
      reverse
      loading={loadingMore}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
      className="SessionChatMessages"
    >
      {messages.map((message, idx) => {
        const content = [];

        if (message.sourceType === 'system') {
          content.push(<SessionChatSystemMessage key={message.id} message={message} />);
        } else if (message.sourceType === 'bot') {
          content.push(
            <SessionChatBotMessage
              key={message.id}
              message={message}
              nextMessage={messages[idx - 1]}
              visitor={visitor}
              members={members}
              isChatCollapsed={isChatCollapsed}
            />,
          );
        } else {
          content.push(
            <SessionChatMessage
              key={message.id}
              message={message}
              nextMessage={messages[idx - 1]}
              visitor={visitor}
              aiPersonaAgent={aiPersonaAgent}
              members={members}
              isChatCollapsed={isChatCollapsed}
            />,
          );
        }

        if (
          !messages[idx + 1] ||
          !isSameDay(message.timestamp, messages[idx + 1]?.timestamp)
        ) {
          content.push(
            <div className="SessionChatMessages__date" key={message.timestamp}>
              <hr />
              <div className="SessionChatMessages__date-content">
                <div>
                  {isSameDay(message.timestamp, new Date())
                    ? 'Today'
                    : format(message.timestamp, 'eee, MMM do')}
                </div>
              </div>
            </div>,
          );
        }

        return content;
      })}
    </FadeScroller>
  );
};
